import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import axiosInstance from '../../utils/axiosInstance';
import { IService, ServicesState } from './services';

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: ServicesState = {
    loading: false,
    error: null,
    currentUserId: null,
    success: false,
    data: null
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const getErrorMessage = (error: any): string => {
    if (axios.isAxiosError(error)) {
        return error.response?.data?.message || error.message || 'An error occurred';
    }
    if (error instanceof Error) {
        return error.message;
    }
    return 'An unknown error occurred';
};

export interface IServicesResponse {
    services: IService[]; // Changed to array
    // Or if your API returns: { service: IService[] }
    // service: IService[];
}

// ============================================================================
// ASYNC THUNKS
// ============================================================================

/**
 * Upsert service (create or update)
 * Accepts FormData with service information and images
 */
export const upsertService = createAsyncThunk<
    IService,
    FormData,
    { rejectValue: string }
>(
    'services/upsertService',
    async (formData: FormData, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('viart_token');

            if (!token) {
                return rejectWithValue('Authentication required');
            }

            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`
                },
                withCredentials: true,
            };

            const { data } = await axiosInstance.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/services/upsert`,
                formData,
                config
            );

            return data.service;
        } catch (error: any) {
            console.error('Upsert error:', error);
            return rejectWithValue(getErrorMessage(error));
        }
    }
);

/**
 * Get service by userId
 * Public endpoint - no authentication required
 */
export const getService = createAsyncThunk<
    IService,
    Record<string, any> | undefined,
    { rejectValue: string }
>(
    'services/getService',
    async (queryParams, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('viart_token');

            const res = await axiosInstance.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/services/get`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    params: queryParams, // undefined is OK
                }
            );

            return res.data.service;
        } catch (error: any) {
            console.error('Get service error:', error);
            return rejectWithValue(getErrorMessage(error));
        }
    }
);

/**
 * Get current user public services by userId
 * Public endpoint - no authentication required
 */
// ============================================

export const getServices = createAsyncThunk<
    IService,
    Record<string, any> | undefined,
    { rejectValue: string }
>(
    'services/getServices',
    async (queryParams, { rejectWithValue }) => {
        try {

            const res = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/services/service`,
                {
                    params: queryParams, // undefined is OK
                }
            );

            return res.data.service;
        } catch (error: any) {
            console.error('Get service error:', error);
            return rejectWithValue(getErrorMessage(error));
        }
    }
);

/**
 * Delete service
 * Requires authentication
 */
export const deleteService = createAsyncThunk<
    { message: string },
    void,
    { rejectValue: string }
>(
    'services/deleteService',
    async (_, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('viart_token');

            if (!token) {
                return rejectWithValue('Authentication required');
            }

            const config = {
                headers: {
                    Authorization: `Bearer ${token}`
                },
                withCredentials: true,
            };

            const { data } = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/services/delete`,
                {},
                config
            );

            return data;
        } catch (error: any) {
            console.error('Delete service error:', error);
            return rejectWithValue(getErrorMessage(error));
        }
    }
);

/**
 * Get all services (optional - for admin)
 * Requires authentication
 */
export const getAllServices = createAsyncThunk<
    IService[],
    void,
    { rejectValue: string }
>(
    'services/getAllServices',
    async (_, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('viart_token');

            if (!token) {
                return rejectWithValue('Authentication required');
            }

            const config = {
                headers: {
                    Authorization: `Bearer ${token}`
                },
                withCredentials: true,
            };

            const { data } = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/services/all`,
                config
            );

            return data.services;
        } catch (error: any) {
            console.error('Get all services error:', error);
            return rejectWithValue(getErrorMessage(error));
        }
    }
);

// ============================================================================
// SLICE
// ============================================================================

const servicesSlice = createSlice({
    name: 'services',
    initialState,
    reducers: {
        /**
         * Reset the entire services state
         */
        resetServicesState: (state) => {
            state.loading = false;
            state.error = null;
            state.success = false;
            state.data = null;
        },

        /**
         * Clear only error state
         */
        clearServicesError: (state) => {
            state.error = null;
        },

        /**
         * Clear only success state
         */
        clearServicesSuccess: (state) => {
            state.success = false;
        },

        /**
         * Update local service data (for optimistic updates)
         */
        updateLocalServiceData: (state, action: PayloadAction<Partial<IService>>) => {
            if (state.data) {
                state.data = { ...state.data, ...action.payload };
            }
        },

        /**
         * Set loading state manually
         */
        setServicesLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },

        setCurrentUserId: (state, action) => {
            state.currentUserId = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // ================================================================
            // UPSERT SERVICE
            // ================================================================
            .addCase(upsertService.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(upsertService.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.error = null;
                state.data = action.payload;
            })
            .addCase(upsertService.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to save service';
                state.success = false;
            })

            // ================================================================
            // GET SERVICE
            // ================================================================
            .addCase(getService.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(getService.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.data = action.payload;
                state.success = true;
            })
            .addCase(getService.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch service';
                state.success = false;
            })

            // ================================================================
            // GET SERVICES
            // ================================================================
            .addCase(getServices.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(getServices.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.data = action.payload;
                state.success = true;
            })
            .addCase(getServices.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch service';
                state.success = false;
            })

            // ================================================================
            // DELETE SERVICE
            // ================================================================
            .addCase(deleteService.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(deleteService.fulfilled, (state) => {
                state.loading = false;
                state.success = true;
                state.error = null;
                state.data = null;
            })
            .addCase(deleteService.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to delete service';
                state.success = false;
            })

            // ================================================================
            // GET ALL SERVICES (Optional)
            // ================================================================
            .addCase(getAllServices.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAllServices.fulfilled, (state) => {
                state.loading = false;
                state.error = null;
                // Note: This doesn't set state.data since it returns an array
                // You might want to add a separate state property for this
            })
            .addCase(getAllServices.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch services';
            });
    },
});

// ============================================================================
// EXPORTS
// ============================================================================

export const {
    resetServicesState,
    clearServicesError,
    clearServicesSuccess,
    setCurrentUserId,
    updateLocalServiceData,
    setServicesLoading
} = servicesSlice.actions;

export default servicesSlice.reducer;

// ============================================================================
// SELECTORS
// ============================================================================

export const selectServicesState = (state: { services: ServicesState }) => state.services;
export const selectServicesLoading = (state: { services: ServicesState }) => state.services.loading;
export const selectServicesError = (state: { services: ServicesState }) => state.services.error;
export const selectServicesSuccess = (state: { services: ServicesState }) => state.services.success;
export const selectServicesData = (state: { services: ServicesState }) => state.services.data;